import { inject, injectable } from 'inversify';
import { TYPES } from '@/engine/ioc';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import type { ViewsService } from '@/engine/app/Views.service';
import type { IObjectViewModel } from '@/engine/app/interfaces';
import { SceneItemKind } from '@/engine/app/interfaces';
import * as DT from '@/engine/PlanningAlgorithm/DataTypes';
import { v4 as uuid } from 'uuid';
import type { ViewEntity } from '@/engine/app/models/viewEntity/view.entity';
import { getMaxIntFromTheEndOfString } from '@/engine/utils/stringUtils';
import type { CasePhaseData } from '@/engine/app/CasePhaseData/CasePhaseData.ts';
import type { MeasurementViewModel } from '@/engine/app/models/MeasurementViewModel';
import { DiametersCommandType, type DiametersCommandsFactory } from './commands/DiametersCommands.factory';
import type { ICommand, ICommandProcessor } from '@/engine/app/modules/core/interfaces/Command';
import { CORE_TYPES } from '@/engine/app/modules/core';

@injectable()
export class AreaDiametersController {
  private readonly _viewsService: ViewsService;

  private readonly _planningAlgorithm: PlanningAlgorithm;

  private readonly _casePhaseData: CasePhaseData;

  private readonly _diametersCommandsFactory: DiametersCommandsFactory;

  private readonly _commandProcessor: ICommandProcessor;

  public constructor(
    @inject(TYPES.ViewsService) viewsService: ViewsService,
    @inject(TYPES.PlanningAlgorithm) planningAlgorithm: PlanningAlgorithm,
    @inject(TYPES.CasePhaseData) casePhaseData: CasePhaseData,
    @inject(TYPES.DiametersCommandsFactory) diametersCommandsFactory: DiametersCommandsFactory,
    @inject(CORE_TYPES.CommandProcessor) commandProcessor: ICommandProcessor
  ) {
    this._viewsService = viewsService;
    this._planningAlgorithm = planningAlgorithm;
    this._casePhaseData = casePhaseData;
    this._diametersCommandsFactory = diametersCommandsFactory;
    this._commandProcessor = commandProcessor;
  }

  public async addDiameter(label: string, originalDescriptor: string): Promise<void> {
    const currentView = this._viewsService.currentView;
    if (!currentView) {
      throw new Error('Current view have to be defined');
    }

    const originalViewModel = currentView
      .getMeasurements()
      .find((object) => object.kind == SceneItemKind.DiameterMeasurement && object.descriptor == originalDescriptor);
    if (!originalViewModel) {
      throw new Error('View Model is expected to be existent');
    }

    const originalAreaModel = this._planningAlgorithm.getValue(originalDescriptor, DT.AreaMeasurement);
    if (originalAreaModel === undefined) {
      throw new Error('Original Area model is undefined');
    }

    const customDescriptor = 'CustomDiameter' + uuid();
    const customLabel = `${label} ${this._getMaxCustomMeasurement(currentView) + 1}`;

    const viewModel = originalViewModel.clone() as MeasurementViewModel;
    viewModel.props.descriptor = customDescriptor;
    viewModel.props.attachments = [];
    viewModel.props.label = customLabel;
    viewModel.props.isTunable = false;
    viewModel.props.imported = false;
    viewModel.props.readyForSliding = false;

    const command: ICommand = this._diametersCommandsFactory.createCommand(
      currentView,
      customDescriptor,
      originalAreaModel,
      viewModel,
      DiametersCommandType.AddPreset
    );
    await this._commandProcessor.execute(command);
  }

  public getCustomDiameterDescriptor(): string | undefined {
    const viewObject: IObjectViewModel | undefined = this._viewsService.currentView
      ?.getObjects()
      .find((object) => object.kind === SceneItemKind.CustomShiftingPlane);
    return viewObject?.attachments.at(0);
  }

  private _getMaxCustomMeasurement(view: ViewEntity): number {
    const customMeasurements = view
      .getMeasurements()
      .filter((measurement) => measurement.imported === false)
      .map((m) => m.label);
    return getMaxIntFromTheEndOfString(customMeasurements);
  }

  public async deleteDiameterByDescriptor(descriptor: string): Promise<void> {
    const measurement = this._viewsService.currentView?.getMeasurements().find((m) => m.descriptor === descriptor);
    if (!measurement) return;

    const originalAreaModel = this._planningAlgorithm.getValue(descriptor, DT.AreaMeasurement);
    if (originalAreaModel === undefined) {
      throw new Error('Original Area model is undefined');
    }

    const command: ICommand = this._diametersCommandsFactory.createCommand(
      this._viewsService.currentViewOrThrow,
      descriptor,
      originalAreaModel,
      measurement,
      DiametersCommandType.RemovePreset
    );
    await this._commandProcessor.execute(command);
  }

  public setDiameterLabel(descriptor: string, label: string): void {
    const measurement = this._viewsService.currentView?.getMeasurements().find((m) => m.descriptor === descriptor);
    if (!measurement) return;
    measurement.setLabel(label);
  }
}
